import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar: React.FC = () => {
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLanguageChange = (event: any) => {
    setLanguage(event.target.value);
  };

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
    // Scroll to top when navigating to new page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMobileMenuToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileNavClick = (action: () => void) => {
    action();
    setMobileOpen(false);
  };

  return (
    <AppBar position="fixed" elevation={0}>
      <Container maxWidth="lg">
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => handleNavigation('/')}
          >
            <img
              src={require('../assets/onionshack-logo.png')}
              alt="Onionshack Logo"
              style={{
                height: '40px',
              }}
            />
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
            <Button
              onClick={() => scrollToSection('home')}
              sx={{ color: '#1A2332', fontWeight: 500 }}
            >
              {t.nav.home}
            </Button>
            <Button
              onClick={() => scrollToSection('about')}
              sx={{ color: '#1A2332', fontWeight: 500 }}
            >
              {t.nav.about}
            </Button>
            <Button
              onClick={() => scrollToSection('services')}
              sx={{ color: '#1A2332', fontWeight: 500 }}
            >
              {t.nav.services}
            </Button>
            <Button
              onClick={() => handleNavigation('/portfolio')}
              sx={{ color: '#1A2332', fontWeight: 500 }}
            >
              {language === 'th' ? 'ผลงาน' : 'Portfolio'}
            </Button>
            <Button
              onClick={() => handleNavigation('/rootbot')}
              sx={{
                fontWeight: 600,
                color: '#6366f1',
                '&:hover': { backgroundColor: 'rgba(99,102,241,0.06)' },
              }}
            >
              rootBot
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              sx={{ color: '#1A2332', fontWeight: 500 }}
            >
              {t.nav.contact}
            </Button>

            <FormControl size="small" sx={{ minWidth: 80, ml: 2 }}>
              <Select
                value={language}
                onChange={handleLanguageChange}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSelect-select': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#1A2332',
                  },
                }}
              >
                <MenuItem value="th">TH</MenuItem>
                <MenuItem value="en">EN</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Mobile Menu Button */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <Select
                value={language}
                onChange={handleLanguageChange}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSelect-select': {
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#1A2332',
                    p: 1,
                  },
                }}
              >
                <MenuItem value="th">TH</MenuItem>
                <MenuItem value="en">EN</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              onClick={handleMobileMenuToggle}
              aria-label={language === 'th' ? 'เปิดเมนู' : 'Open menu'}
              sx={{ color: '#1A2332' }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 250,
            backgroundColor: '#ffffff',
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <img
            src={require('../assets/onionshack-logo.png')}
            alt="Onionshack Logo"
            style={{ height: '30px' }}
          />
          <IconButton
            onClick={() => setMobileOpen(false)}
            aria-label={language === 'th' ? 'ปิดเมนู' : 'Close menu'}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileNavClick(() => scrollToSection('home'))}>
              <ListItemText primary={t.nav.home} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileNavClick(() => scrollToSection('about'))}>
              <ListItemText primary={t.nav.about} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileNavClick(() => scrollToSection('services'))}>
              <ListItemText primary={t.nav.services} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileNavClick(() => handleNavigation('/portfolio'))}>
              <ListItemText primary={language === 'th' ? 'ผลงาน' : 'Portfolio'} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileNavClick(() => handleNavigation('/rootbot'))}>
              <ListItemText primary="rootBot" primaryTypographyProps={{ sx: { color: '#6366f1', fontWeight: 600 } }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileNavClick(() => scrollToSection('contact'))}>
              <ListItemText primary={t.nav.contact} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </AppBar>
  );
};

export default Navbar;